import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Eyebrow, Button } from "../components/Bits";
import Editable from "../components/Editable";
import ProjectGallery from "../components/ProjectGallery";
import { IMG, GALLERIES } from "../lib/content";

const sponsors = [
  "Energy Coast UTC", "Westinghouse", "The Cumberland", "BEC", "Six Social Impact",
  "Prospect", "TEG Retail CIC", "Test Inspect", "Illumination Events", "Dobies Cumbria",
  "Carlos Reina Photography", "FlexiRental", "Street Food", "Harry and Jack", "Wimba",
  "Whitehaven AFC", "Proud & Diverse Cumbria", "YAZ", "Lakeland Life",
];

const years = [
  { year: "2026", body: "Energy Coast LIVE! 2026 welcomed hundreds of people to Energy Coast UTC for a day celebrating live music, opportunity, community and youth leadership. Planned and delivered by young people, the festival showed what happens when young people are trusted to lead." },
  { year: "2025", body: "The 2025 edition set the foundations, bringing nationally recognised artists together with the best local performers and proving there was a real appetite for a young-people-led festival on the Energy Coast." },
];

export default function EnergyCoastLive() {
  return (
    <div>
      <section className="relative overflow-hidden bg-nead-ink text-white pt-[74px]">
        <div className="absolute inset-0">
          <img src={IMG.energyCoast} alt="" className="h-full w-full object-cover opacity-25 grayscale" />
          <div className="absolute inset-0 bg-gradient-to-t from-nead-ink via-nead-ink/85 to-nead-ink/60" />
        </div>
        <div className="relative container-x pt-14 pb-16 md:pt-20 md:pb-20">
          <Link to="/work" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-6"><ArrowLeft className="h-4 w-4" /> Our Work</Link>
          <Eyebrow className="text-nead-cyan">Live music · Youth-led festival</Eyebrow>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-5 font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.03] max-w-4xl">
            Energy Coast <span className="text-gradient">LIVE!</span>
          </motion.h1>
          <p className="mt-6 max-w-2xl text-lg text-white/70 leading-relaxed">
            A major live music festival planned and delivered by young people through NEAD CIC, in partnership with Energy Coast UTC.
          </p>
        </div>
      </section>

      <section className="container-x py-20 md:py-28">
        <div className="max-w-3xl">
          <Reveal>
            <span className="inline-block rounded-full bg-nead-pink/10 px-4 py-1.5 text-sm font-semibold text-nead-pink">Energy Coast LIVE! 2026 has now concluded</span>
            <div className="mt-8 space-y-5 text-nead-navy/70 text-lg leading-relaxed">
              <Editable id="ecl.thanks.p1" as="p" multiline>Thank you to everyone who attended, performed, volunteered, exhibited, traded and supported this year's festival. Energy Coast LIVE! 2026 welcomed hundreds of people to Energy Coast UTC for a day celebrating live music, opportunity, community and youth leadership.</Editable>
              <Editable id="ecl.thanks.p2" as="p" multiline>Planned and delivered by young people through Northern Equality and Diversity (NEAD CIC) in partnership with Energy Coast UTC, the festival showcased what can be achieved when young people are trusted to lead. We are incredibly proud of everyone who helped make the event such a success.</Editable>
              <Editable id="ecl.thanks.p3" as="p" multiline>Our team is now reviewing this year's festival and beginning discussions about what comes next.</Editable>
            </div>
            <p className="mt-8 font-display font-bold text-nead-navy">Rocco Boyd<span className="block text-sm font-medium text-nead-navy/50">Chairman, NEAD CIC</span></p>
          </Reveal>
        </div>
      </section>

      <section className="bg-nead-mist">
        <div className="container-x py-20 md:py-28">
          <Reveal><Eyebrow className="text-nead-pink">Gallery</Eyebrow>
          <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl tracking-tight text-nead-navy mb-10">On the day.</h2></Reveal>
          <ProjectGallery galleryKey="ecl" fallback={GALLERIES.ecl} label="Energy Coast LIVE!" />
        </div>
      </section>

      <section className="container-x py-20 md:py-28">
        <Reveal><Eyebrow className="text-nead-pink">The story so far</Eyebrow>
        <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl tracking-tight text-nead-navy max-w-2xl">Two years of Energy Coast LIVE!</h2></Reveal>
        <Stagger className="mt-12 grid md:grid-cols-2 gap-6">
          {years.map((y) => (
            <StaggerItem key={y.year}>
              <div className="rounded-[1.5rem] border border-black/[0.07] p-8 h-full">
                <span className="font-display font-bold text-5xl text-gradient">{y.year}</span>
                <p className="mt-4 text-nead-navy/65 leading-relaxed">{y.body}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <section className="bg-nead-ink text-white">
        <div className="container-x py-20 md:py-24">
          <Reveal><Eyebrow className="text-nead-cyan">Thank you to our sponsors &amp; partners</Eyebrow>
          <p className="mt-5 text-white/60 max-w-2xl">The festival would not have been possible without the generosity of our sponsors, partners and supporters who contributed funding, equipment, services, expertise or their time, enabling us to deliver a free, professionally managed event for the community.</p></Reveal>
          <Stagger className="mt-10 flex flex-wrap gap-3">
            {sponsors.map((s) => (
              <StaggerItem key={s}>
                <span className="inline-block rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-white/85">{s}</span>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="container-x py-20 md:py-28 text-center">
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-nead-navy">Want to support the next one?</h2>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button to="/partners" variant="pink" testId="ecl-partner">Become a sponsor</Button>
          <Button to="/events" variant="outline" testId="ecl-events">What's on next</Button>
        </div>
      </section>
    </div>
  );
}
